import { Box, CircularProgress, Typography } from "@mui/material"
import Grid from "@mui/material/Grid"
import ScenarioTree, { DefaultSelection } from "@components/ScenarioTree"
import React, { useEffect, useMemo, useState } from "react"
import { useLazyGetRunGroupsByFamilyQuery } from "@store/services/runService"
import RunGroupTable from "@features/scenarioDesign/RunGroupTable"
import { useSearchParams } from "react-router"
import { RunGroup, RunGroupStatus } from "@custom-types/modelRun"

export default function Results() {
  const [defaultSelection, setDefaultSelection] = useState<DefaultSelection>()
  const [searchParams, setSearchParams] = useSearchParams({})
  const [familyKey, setFamilyKey] = useState("")
  const [runGroupToHighlightKey, setRunGroupToHighlightKey] = useState<string | undefined>(
    undefined,
  )
  const [fetchRunGroups, { data: runGroups, isFetching, isError }] =
    useLazyGetRunGroupsByFamilyQuery()

  const handleFamilySelected = (familyKey: string) => {
    setFamilyKey(familyKey)
  }
  useEffect(() => {
    if (familyKey) {
      ;(async () => {
        await fetchRunGroups(familyKey)
        highlightRunGroup()
      })()
      // https://stackoverflow.com/a/70330406
      setSearchParams((searchParams) => {
        searchParams.set("familyKey", familyKey)
        return searchParams
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [familyKey])

  const highlightRunGroup = () => {
    const key = searchParams.get("runGroupToHighlightKey")
    if (!key) return
    setRunGroupToHighlightKey(key)
    setTimeout(() => {
      setSearchParams((searchParams) => {
        searchParams.delete("runGroupToHighlightKey")
        return searchParams
      })
      setRunGroupToHighlightKey(undefined)
    }, 5000) // clear selection after 5 seconds
  }

  const filteredResults = useMemo(() => {
    if (searchParams.get("filter") === "all") return runGroups?.items || []
    return runGroups?.items?.filter((x: RunGroup) => x.status === RunGroupStatus.DONE) || []
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [runGroups])

  useEffect(() => {
    const graphKeyParam = searchParams.get("familyKey")
    if (!graphKeyParam) {
      return
    }

    const [organisationKey, divisionKey, productKey, familyKey] =
      decodeURIComponent(graphKeyParam).split("#")
    if (
      defaultSelection &&
      defaultSelection.organisationKey === organisationKey &&
      defaultSelection.divisionKey === divisionKey &&
      defaultSelection.productKey === productKey &&
      defaultSelection.familyKey === familyKey
    )
      return
    setDefaultSelection({
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      organisationKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      divisionKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      productKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      familyKey,
      version: "",
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [searchParams])

  if (!familyKey) {
    return (
      <Box sx={{ mt: 0 }}>
        <Grid container sx={{ height: "calc(100vh - 64px)", pl: 0 }} wrap="nowrap" gap={1}>
          <Grid item xs="auto">
            <ScenarioTree
              selectedFamilyKey={familyKey}
              onProductFamilySelect={handleFamilySelected}
              defaultSelection={defaultSelection}
              roleHint={"whatif"}
            />
          </Grid>
          <Grid
            container
            item
            sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}
          >
            <Grid item>
              <Typography variant={"h6"}>Please select a graph family from the sidebar</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 0 }}>
      <Grid container sx={{ height: "calc(100vh - 64px)", pl: 0 }} wrap="nowrap" gap={1}>
        <Grid item xs="auto">
          <ScenarioTree
            selectedFamilyKey={familyKey}
            onProductFamilySelect={handleFamilySelected}
            defaultSelection={defaultSelection}
            roleHint={"whatif"}
          />
        </Grid>
        <Grid item xs gap={1}>
          {isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <RunGroupTable
              runGroups={filteredResults}
              runGroupToHighlightKey={runGroupToHighlightKey}
              sessionStorageName="results-page-run-groups"
            />
          )}
        </Grid>
      </Grid>
    </Box>
  )
}
